import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const decodeJWT = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const warningTimerRef = useRef(null);
  const expiryTimerRef  = useRef(null);

  const clearSessionTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(expiryTimerRef.current);
  }, []);

  const doLogout = useCallback((redirect = true) => {
    clearSessionTimers();
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    if (redirect) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?returnUrl=${returnUrl}`;
    }
  }, [clearSessionTimers]);

  const setupSessionTimers = useCallback((tkn) => {
    clearSessionTimers();
    const payload = decodeJWT(tkn);
    if (!payload?.exp) return;

    const expMs        = payload.exp * 1000;
    const now          = Date.now();
    const msUntilExp   = expMs - now;
    const msUntilWarn  = msUntilExp - 5 * 60 * 1000; // 5 min antes

    if (msUntilExp <= 0) {
      doLogout();
      return;
    }

    if (msUntilWarn > 0) {
      warningTimerRef.current = setTimeout(() => {
        toast.warning('Tu sesión expira en 5 minutos. Guarda tu trabajo.', {
          duration: 60000,
          id: 'session-warning',
        });
      }, msUntilWarn);
    }

    expiryTimerRef.current = setTimeout(() => {
      toast.error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.', { id: 'session-expired' });
      setTimeout(() => doLogout(), 1500);
    }, msUntilExp);
  }, [clearSessionTimers, doLogout]);

  // Interceptor global para 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401 && localStorage.getItem('token')) {
          doLogout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [doLogout]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    const storedUser  = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser && storedToken) {
      const payload = decodeJWT(storedToken);
      if (payload?.exp && payload.exp * 1000 > Date.now()) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        setupSessionTimers(storedToken);
      } else {
        // Token ya expirado al cargar la app
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      setUser(userData);
      setToken(access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', access_token);
      setupSessionTimers(access_token);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Error al iniciar sesión' };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      await axios.post(`${API}/auth/register`, { email, password, name, role });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Error al registrar' };
    }
  };

  const updateUser = (updatedData) => {
    setUser((prev) => {
      const merged = { ...prev, ...updatedData };
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    });
  };

  const logout = () => doLogout(false);

  return (
    <AuthContext.Provider value={{ user, login, logout, register, loading, token, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};
